import React, { memo, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { ApprovalWorkflowItemDto, AWStageDto } from '@api/Api';
import UserAvatar from '@components/UserAvatar';
import { ReactComponent as ImgSvg } from '@assets/icons/img.svg';
import { ReactComponent as CalendarSvg } from '@assets/icons/panel-calendar-icon.svg';
import { ReactComponent as ErrorSvg } from '@assets/icons/contributors_limit_error_notification.svg';
import { ReactComponent as CalendarTwoSvg } from '@assets/icons/calendar-v2.svg';
import { toggleApprovalModal } from '@redux/actions/modalAction';

import './CampaignApprovalInfo.less';

type CampaignApprovalInfoProps = {
  approvalWorkflow: ApprovalWorkflowItemDto;
  currentStage: AWStageDto | null;
  isPastDueDate: boolean;
  refetchApprovalWorkflow: () => void;
};

export default memo(function CampaignApprovalInfo({
  approvalWorkflow,
  currentStage,
  isPastDueDate,
  refetchApprovalWorkflow
}: CampaignApprovalInfoProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const owner = approvalWorkflow?.owner?.user || null;
  const canManage = approvalWorkflow.permissions.manage;

  const progressMedia = useMemo(() => {
    if (approvalWorkflow.isPaused) return [];
    if (approvalWorkflow.isFinished) return [];
    if (!approvalWorkflow.assets?.length) return [];
    if (!currentStage?.id) return [];
    return approvalWorkflow.assets.filter((asset) =>
      (asset?.statuses || []).some(
        (item) =>
          item.stageId === currentStage.id &&
          ['in_progress', 'pending'].includes(item.status)
      )
    );
  }, [
    approvalWorkflow.isPaused,
    approvalWorkflow.isFinished,
    approvalWorkflow.assets,
    currentStage?.id
  ]);

  const onOpenAsset = () => {
    const asset = progressMedia?.[0];
    if (!asset) return;
    const assetUrl = asset.hasAccess
      ? `/media-viewer/${asset.id}/${asset.versionId}`
      : `/approval?id=${approvalWorkflow.id}&assetId=${asset.id}`;
    navigate(assetUrl, {
      state: {
        returnTo: location.pathname + location.search
      }
    });
  };

  const onEdit = () => {
    if (!approvalWorkflow) return;
    if (!canManage) return;
    dispatch(
      toggleApprovalModal({
        visible: true,
        permissions: {
          create: true
        },
        openStep: 'stages',
        campaignId: approvalWorkflow.campaignId,
        approvalWorkflowId: approvalWorkflow.id,
        callback: (aw) => {
          if (aw) refetchApprovalWorkflow();
        }
      })
    );
  };

  return (
    <div className="campaign-approval_info">
      <div className="campaign-approval_info-block">
        <div className="campaign-approval_info-label">Stage</div>
        <div className="campaign-approval_info-progress">
          {!currentStage && '-'}
          {!!currentStage && (
            <>
              <div className="campaign-approval_info-text campaign-approval_info-progress-name">
                {currentStage.name}
              </div>
              <div
                className={classNames('campaign-approval_info-progress-lines', {
                  'b-lines--pause': approvalWorkflow.isPaused
                })}
              >
                {approvalWorkflow.stages.map((item, index) => (
                  <Tooltip
                    title={
                      <>
                        <div className="campaign-approval_info-progress-tooltip_label">
                          Stage: {index + 1}/{approvalWorkflow.stages.length}
                        </div>
                        <div className="campaign-approval_info-progress-tooltip_text">
                          {item.name}
                        </div>
                      </>
                    }
                    overlayClassName="toggle_favorite_overlay campaign-approval_info-progress-tooltip"
                    key={item.id}
                    overlayInnerStyle={{
                      maxWidth: 550
                    }}
                  >
                    <div
                      className={classNames(
                        'campaign-approval_info-progress-line',
                        {
                          'b-line--active': item.id === currentStage?.id,
                          'b-line--finished': item.isFinished,
                          'b-line--approved': item.status === 'approved',
                          'b-line--approved-with-feedback':
                            item.status === 'approved_with_feedback',
                          'b-line--changes-required':
                            item.status === 'changes_required',
                          'b-line--overdue':
                            isPastDueDate && item.id === currentStage?.id
                        }
                      )}
                    />
                  </Tooltip>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="campaign-approval_info-block">
        <div className="campaign-approval_info-label">Deadline</div>
        <Tooltip
          title={currentStage?.deadline ? 'Edit deadline' : 'Set deadline'}
          overlayClassName="toggle_favorite_overlay"
          placement="topLeft"
          trigger={canManage ? undefined : []}
        >
          <div
            className={classNames(
              'campaign-approval_info-text campaign-approval_info-date',
              {
                'b-date--past': isPastDueDate
              }
            )}
            onClick={onEdit}
          >
            {!currentStage?.deadline && (
              <>
                <CalendarTwoSvg />
                No deadline
              </>
            )}
            {!!currentStage?.deadline && (
              <>
                <CalendarSvg />
                {moment(currentStage.deadline).format('MMM DD')}
                {isPastDueDate && (
                  <ErrorSvg
                    width={16}
                    height={16}
                    style={{ marginLeft: '4px' }}
                  />
                )}
              </>
            )}
          </div>
        </Tooltip>
      </div>
      <div className="campaign-approval_info-block">
        <div className="campaign-approval_info-label">in review media</div>
        <Tooltip
          title="Open in media viewer"
          overlayClassName="toggle_favorite_overlay"
          className="campaign-approval_info-media"
          trigger={progressMedia.length ? undefined : []}
          placement="topLeft"
        >
          <div onClick={onOpenAsset}>
            <ImgSvg />
            {progressMedia.length || 0}
          </div>
        </Tooltip>
      </div>
      <div className="campaign-approval_info-block">
        <div className="campaign-approval_info-label">Manager</div>
        <div className="campaign-approval_info-text campaign-approval_info-manager">
          {!owner && '-'}
          {!!owner && (
            <>
              <div className="campaign-approval_info-manager-avatar">
                <UserAvatar
                  size={32}
                  isActive
                  src={owner?.picture.url}
                  userName={owner?.name || ''}
                  userEmail={owner?.email || ''}
                />
              </div>
              <div className="campaign-approval_info-manager-name">
                {owner?.name}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});
